/**
 * NationalEducationPage — 國民教育微課課程包 (the KS2 / KS3 course pack).
 *
 * WHAT CHANGED (2026-11-03)
 * -------------------------
 * This page used to be a hand-coded STANDALONE document:
 * `public/products/national-education/index.html` — 1,063 lines with 673
 * lines of inline CSS, served by nginx straight out of `dist/`. It had no
 * site chrome at all: no navbar, no footer, no language switcher. It also
 * existed outside the React Router, so it could not share the site's
 * navigation, could not be reached through the SPA, and needed its own
 * hand-maintained copy of the canonical/hreflang/JSON-LD head.
 *
 * It is now a React route under `PublicLayout`, exactly like
 * `/products/smart-cindy`, so it gains the YADA header bar (CMS-driven
 * menu, dropdowns, mobile drawer, language switcher) and the footer.
 *
 * THE CONTENT IS UNCHANGED
 * ------------------------
 * The copy still lives in Chinese in EVERY locale — that is the site's
 * standing decision for this page (there is no English version of the
 * landing page). The /en/ URL shows the Chinese text, and /zh-Hans/ shows
 * the Simplified conversion, exactly as the mirrored standalone files did.
 *
 * That is why this page reads its strings with an EXPLICIT `lng` rather
 * than the ambient i18next language: `fallbackLng` is 'en', so a missing
 * key would silently print the key name instead of falling back to
 * zh-Hant. `contentLng` below is 'zh-Hans' for the Simplified copy and
 * 'zh-Hant' for everything else.
 *
 * The stylesheet is imported from src/main.tsx, NOT from here — the
 * prerender pass renders this component in plain Node via `tsx`, and Node
 * cannot import a `.css` file.
 */
import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { SITE } from '@/content/site'
import { buildCanonicalUrl, SITE_NAME, SITE_URL } from '@/utils/siteMeta'
import { buildLocalePath, parseLocaleFromPath } from '@/utils/localePath'
import SEO from '@/utils/seo'
import { buildBreadcrumbList } from '@/utils/structuredData'
import { useSlotT, useSlotObject } from '@/cms/useSlotT'

interface StatItem {
  num: string
  lbl: string
}

interface PainItem {
  strong: string
  text: string
}

interface PillarRow {
  name: string[]
  ks2: string
  ks3: string
}

interface LengthCard {
  stage: string
  val: string
  unit: string
  desc: string
}

interface FormulaPart {
  text: string
  op?: string
}

interface FormatPart {
  text: string
  hl: boolean
}

interface SampleOption {
  key: string
  text: string
  correct?: boolean
}

interface SampleItem {
  meta: string
  text: string
  options: SampleOption[]
}

interface ScenarioItem {
  tag: string
  title: string
  text: string
}

export default function NationalEducationPage() {
  const { i18n, t } = useTranslation()
  const { pathname } = useLocation()

  // The landing page is Chinese in every locale. zh-Hans reads the
  // Simplified strings; zh-Hant AND en both read the Traditional ones
  // (there is no English copy of this page).
  const contentLng = i18n.language === 'zh-Hans' ? 'zh-Hans' : 'zh-Hant'

  /** Look a string up in the page's content locale, not the ambient one. */
  const tr = (key: string) => t(key, { lng: contentLng })

  // CMS slot hooks — explicit locale to match the page's content language
  const slotT = useSlotT('national-education', contentLng)
  const slotObj = useSlotObject('national-education', contentLng)

  const seo = {
    title: slotT('seo.nationalEducationTitle'),
    description: slotT('seo.nationalEducationDescription'),
    keywords: slotT('seo.nationalEducationKeywords'),
  }

  const hero = slotObj('nationalEducation.landing.hero') as {
    tag: string
    titleLead: string
    titleAccent: string
    sub: string
    stats: StatItem[]
  }

  const pain = slotObj('nationalEducation.landing.pain') as {
    eyebrow: string
    title: string
    leadLine1: string
    leadLine2Before: string
    leadLine2Strong: string
    leadLine2After: string
    items: PainItem[]
    solutionPrefix: string
    solutionA: string
    solutionMid: string
    solutionB: string
    solutionText: string
  }

  const advantages = slotObj('nationalEducation.landing.advantages') as {
    eyebrow: string
    title: string
    one: {
      num: string
      title: string
      tableHead: string[]
      rows: PillarRow[]
      sum: string
    }
    two: {
      num: string
      title: string
      lengths: LengthCard[]
      leadBefore: string
      leadStrong: string
      leadAfter: string
      uses: string[]
      quote: string
    }
    three: {
      num: string
      title: string
      badge: string
      strong: string
      text: string
      formulaParts: FormulaPart[]
      lead: string
    }
    four: {
      num: string
      title: string
      lead: string
      formatParts: FormatPart[]
    }
  }

  const samples = slotObj('nationalEducation.landing.samples') as {
    eyebrow: string
    titleLead: string
    titleAccent: string
    intro: string
    correctAnswer: string
    tryTitle: string
    trySub: string
    tryBtn: string
    items: SampleItem[]
  }

  const scenarios = slotObj('nationalEducation.landing.scenarios') as {
    eyebrow: string
    title: string
    items: ScenarioItem[]
  }

  const why = slotObj('nationalEducation.landing.why') as {
    eyebrow: string
    titleLead: string
    titleAccent: string
    titleTail: string
    textBefore: string
    textStrong: string
    textAfter: string
    highlightBig: string
    highlightSmall: string
  }

  const breadcrumb = slotObj('nationalEducation.landing.breadcrumb') as {
    home: string
    products: string
    self: string
  }

  const canonicalUrl = buildCanonicalUrl(pathname)

  /**
   * The question-bank sample page. It is a sibling React route
   * (`/products/national-education/KS2-v3.html`) and the link points at the
   * CURRENT locale's copy, so an English reader lands on the English
   * sample and a Simplified-Chinese reader on the Simplified one. The
   * landing page's own copy stays Chinese in every locale, but the sample
   * page really is translated.
   */
  const ks2Path = buildLocalePath(
    '/products/national-education/KS2-v3.html',
    parseLocaleFromPath(pathname),
  )

  return (
    <>
      <SEO
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: seo.title,
            description: seo.description,
            url: canonicalUrl,
            inLanguage: ['zh-Hant', 'zh-Hans', 'en'],
            publisher: {
              '@type': 'Organization',
              name: SITE_NAME,
              url: `${SITE_URL}/`,
            },
          },
          buildBreadcrumbList([
            { name: breadcrumb.home, path: '/' },
            { name: breadcrumb.products, path: '/products' },
            { name: breadcrumb.self, path: '/products/national-education' },
          ]),
        ]}
      />

      <div className="ne-root">
        <div className="page">
          {/* ── Hero ── */}
          <header className="hero">
            <span className="hero-tag">{hero.tag}</span>
            <h1>
              {hero.titleLead}
              <br />
              <span className="accent">{hero.titleAccent}</span>
            </h1>
            <p className="sub">{hero.sub}</p>

            <div className="hero-stats">
              {hero.stats.map((stat) => (
                <div className="stat" key={stat.lbl}>
                  <div className="num">{stat.num}</div>
                  <div className="lbl">{stat.lbl}</div>
                </div>
              ))}
            </div>

            <div className="hero-cta">
              <Link className="try-btn" to={ks2Path} data-discover="true">
                {samples.tryBtn}
                <span aria-hidden="true">→</span>
              </Link>
            </div>
          </header>

          {/* ── The pain ── */}
          <section>
            <div className="sec-eyebrow">{pain.eyebrow}</div>
            <h2>{pain.title}</h2>

            <p className="pain-lead">
              {pain.leadLine1}
              <br />
              {pain.leadLine2Before}
              <strong style={{ color: 'var(--red-700)' }}>{pain.leadLine2Strong}</strong>
              {pain.leadLine2After}
            </p>

            <div className="pain-list">
              {pain.items.map((item) => (
                <div className="pain" key={item.strong}>
                  <strong>{item.strong}</strong>
                  {item.text}
                </div>
              ))}
            </div>

            <div className="solution-band">
              <div className="eq">
                {pain.solutionPrefix}
                <span className="big">{pain.solutionA}</span>
                {pain.solutionMid}
                <span className="big">{pain.solutionB}</span>
              </div>
              <p>{pain.solutionText}</p>
            </div>
          </section>

          {/* ── The four advantages ── */}
          <section className="alt">
            <div className="sec-eyebrow">{advantages.eyebrow}</div>
            <h2>{advantages.title}</h2>

            {/* 1 — the curriculum table */}
            <div className="adv">
              <div className="adv-head">
                <div className="adv-num">{advantages.one.num}</div>
                <h3>{advantages.one.title}</h3>
              </div>

              <table className="pillar-table">
                <thead>
                  <tr>
                    {advantages.one.tableHead.map((head) => (
                      <th key={head} scope="col">
                        {head}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {advantages.one.rows.map((row) => (
                    <tr key={row.name.join('')}>
                      <th scope="row" className="p-name">
                        {row.name.map((line, i) => (
                          <Fragment key={line}>
                            {i > 0 && <br />}
                            {line}
                          </Fragment>
                        ))}
                      </th>
                      <td>{row.ks2}</td>
                      <td>{row.ks3}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="sum-note">{advantages.one.sum}</div>
            </div>

            {/* 2 — the length control */}
            <div className="adv">
              <div className="adv-head">
                <div className="adv-num">{advantages.two.num}</div>
                <h3>{advantages.two.title}</h3>
              </div>

              <div className="length-grid">
                {advantages.two.lengths.map((card) => (
                  <div className="length-card" key={card.stage}>
                    <div className="stage">{card.stage}</div>
                    <div className="val">
                      {card.val} <small>{card.unit}</small>
                    </div>
                    <div className="desc">{card.desc}</div>
                  </div>
                ))}
              </div>

              <p className="lead">
                {advantages.two.leadBefore}
                <strong>{advantages.two.leadStrong}</strong>
                {advantages.two.leadAfter}
              </p>
              <ul className="use-list">
                {advantages.two.uses.map((use) => (
                  <li key={use}>{use}</li>
                ))}
              </ul>

              <div className="quote-line">{advantages.two.quote}</div>
            </div>

            {/* 3 — the built-in assessment */}
            <div className="adv">
              <div className="adv-head">
                <div className="adv-num">{advantages.three.num}</div>
                <h3>{advantages.three.title}</h3>
              </div>

              <div className="q-highlight">
                <div className="q-badge">{advantages.three.badge}</div>
                <div className="txt">
                  <strong>{advantages.three.strong}</strong>
                  {advantages.three.text}
                </div>
              </div>

              <div className="formula">
                {advantages.three.formulaParts.map((part, i) => (
                  <Fragment key={i}>
                    {part.text}
                    {part.op && <span className="op">{part.op}</span>}
                  </Fragment>
                ))}
              </div>

              <p className="lead" style={{ marginTop: '16px' }}>
                {advantages.three.lead}
              </p>
            </div>

            {/* 4 — the delivery format */}
            <div className="adv">
              <div className="adv-head">
                <div className="adv-num">{advantages.four.num}</div>
                <h3>{advantages.four.title}</h3>
              </div>

              <p className="lead" style={{ marginBottom: '16px' }}>
                {advantages.four.lead}
              </p>

              <div className="format-box">
                {advantages.four.formatParts.map((part, i) => (
                  <Fragment key={part.text}>
                    {i > 0 && '｜'}
                    <span className={part.hl ? 'hl' : 'dim'}>{part.text}</span>
                  </Fragment>
                ))}
              </div>
            </div>
          </section>

          {/* ── Sample questions ── */}
          <section>
            <div className="sec-eyebrow">{samples.eyebrow}</div>
            <h2>
              {samples.titleLead}
              <span className="accent">{samples.titleAccent}</span>
            </h2>
            <p className="sample-intro">{samples.intro}</p>

            <div className="q-sample-grid">
              {samples.items.map((item) => (
                <article className="q-card" key={`${item.meta}|${item.text}`}>
                  <span className="q-meta">{item.meta}</span>
                  <p className="q-text">{item.text}</p>
                  <ul className="q-opts">
                    {item.options.map((opt) => (
                      <li className={opt.correct ? 'correct' : undefined} key={opt.key}>
                        {opt.key}. {opt.text}
                        {opt.correct && (
                          <>
                            <span className="mark" aria-hidden="true">
                              ✓
                            </span>
                            <span className="sr-only">{samples.correctAnswer}</span>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>

            {/* ── Try the real thing ── */}
            <div className="try-box">
              <div>
                <span className="try-title">{samples.tryTitle}</span>
                <span className="try-sub">{samples.trySub}</span>
              </div>
              <Link className="try-btn" to={ks2Path}>
                {samples.tryBtn}
                <span aria-hidden="true">→</span>
              </Link>
            </div>
          </section>

          {/* ── Where it is used ── */}
          <section className="alt">
            <div className="sec-eyebrow">{scenarios.eyebrow}</div>
            <h2>{scenarios.title}</h2>

            <div className="scenarios">
              {scenarios.items.map((scene) => (
                <div className="scene" key={scene.tag}>
                  <span className="tag">{scene.tag}</span>
                  <h4>{scene.title}</h4>
                  <p>{scene.text}</p>
                </div>
              ))}
            </div>
          </section>

          {/* ── Why now ── */}
          <section className="why">
            <div className="sec-eyebrow" style={{ color: 'var(--gold-light)' }}>
              {why.eyebrow}
            </div>
            <h2>
              {why.titleLead}
              <span className="accent">{why.titleAccent}</span>
              {why.titleTail}
            </h2>

            <p>
              {why.textBefore}
              <strong>{why.textStrong}</strong>
              {why.textAfter}
            </p>

            <div className="highlight-box">
              <div className="big">{why.highlightBig}</div>
              <div className="small">{why.highlightSmall}</div>
            </div>

            {/*
              2026-11-04 — the 產品頁 closing CTA band, copied over AS-IS
              from
              `#main-content > section.relative.py-section-y…overflow-hidden >
               div.mx-auto…relative.z-10 > div > div > div`
              (i.e. the inner `div.text-center.max-w-3xl.mx-auto` of
              src/components/home/CtaBanner.tsx, as it renders on
              /products/). No component was extracted and no other page was
              touched — the block is inlined here, at the end of the 「為什麼
              是現在」 section.

              Three things changed on the way in, and only these three:

              1. COPY — the source reads its text through the CMS slot
                 `useSlotT('products')`, which resolves in the VISITOR's
                 locale (English on /en/). This page is deliberately
                 Chinese-only (see the file header), so the same four
                 strings are read with `tr()` instead, which pins them to
                 `contentLng` — the copy is identical to the one the 產品頁
                 band shows, and Chinese on every locale, like the rest of
                 the page. The i18n keys are unchanged (`home.cta.*`).

              2. HREFS — both links are absolute and locale-independent, so
                 nothing had to be re-pointed: the WhatsApp button keeps
                 `SITE.contact.whatsapp.link` and the mail button keeps
                 `SITE.contact.email.link`. (A root-relative path such as
                 `/contact` would have needed `buildLocalePath('/contact',
                 locale)` here — this page is served under /zh-Hant/,
                 /zh-Hans/ and /en/, and a bare `/contact` would drop the
                 reader out of their locale.)

              3. `id="cta-title"` — dropped. It was the `aria-labelledby`
                 target of the source `<section>`, which is not coming along;
                 the `<section className="why">` that wraps this block has
                 its own `<h2>`.
            */}
            <div className="cta-band text-center max-w-3xl mx-auto">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4 leading-tight text-balance">
                {tr('home.cta.title')}
              </h2>
              <p className="text-base md:text-xl text-surface/90 mb-6 md:mb-8 text-pretty">
                {tr('home.cta.text')}
              </p>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
                <a
                  href={SITE.contact.whatsapp.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-accent text-ink px-5 py-3 md:px-8 md:py-4 rounded-yada-full hover:bg-accent-soft transition-all hover:-translate-y-0.5 text-sm md:text-base font-semibold shadow-yada-lg"
                >
                  <Icon icon="lucide:message-circle" style={{ fontSize: '1.25rem' }} />
                  <span>{tr('home.cta.button')}</span>
                </a>
                <a
                  href={SITE.contact.email.link}
                  className="inline-flex items-center justify-center gap-2 bg-transparent text-surface border-2 border-surface px-5 py-3 md:px-8 md:py-4 rounded-yada-full hover:bg-surface hover:text-primary transition-all text-sm md:text-base font-semibold"
                >
                  <Icon icon="lucide:mail" style={{ fontSize: '1.25rem' }} />
                  <span>{tr('home.cta.emailCta')}</span>
                </a>
              </div>
            </div>

            {/* Back to Products button */}
            <div className="back-to-products text-center mt-12">
              <Link
                to="/products"
                className="inline-flex items-center gap-2 text-ink-soft hover:text-primary transition-colors font-medium focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"
              >
                <Icon icon="lucide:arrow-left" className="text-base" aria-hidden="true" />
                <span>{tr('products.backToProducts')}</span>
              </Link>
            </div>
          </section>
        </div>
      </div>
    </>
  )
}
